import React, { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageLayout from "./PageLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your message has been sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout
      title="Contact Us"
      subtitle="We'd love to hear from you. Whether you have a question, a testimony, or a suggestion, our team is glad to help."
    >
      <div className="max-w-5xl mx-auto px-5 py-14 md:py-20 grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-12">
        {/* Info */}
        <div>
          <h2 className="font-serif-display text-[#222] text-xl font-bold mb-5">
            Get in touch
          </h2>
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-[#eaf6f8] flex items-center justify-center shrink-0">
                <Mail size={18} className="text-[#0f9fbc]" />
              </span>
              <div>
                <p className="font-serif-body text-[#333] font-bold text-[15px]">Email</p>
                <p className="font-serif-body text-[#666] text-[15px]">hello@lifewithabba.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-[#eaf6f8] flex items-center justify-center shrink-0">
                <MessageCircle size={18} className="text-[#0f9fbc]" />
              </span>
              <div>
                <p className="font-serif-body text-[#333] font-bold text-[15px]">Community</p>
                <p className="font-serif-body text-[#666] text-[15px]">
                  Join our Facebook Group to connect with others sharing their
                  #LifeWithAbba.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block font-serif-body text-[13px] text-[#555] mb-1">Name</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full font-serif-body text-[15px] px-4 py-3 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] focus:border-transparent"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block font-serif-body text-[13px] text-[#555] mb-1">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                className="w-full font-serif-body text-[15px] px-4 py-3 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block font-serif-body text-[13px] text-[#555] mb-1">Subject</label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                className="w-full font-serif-body text-[15px] px-4 py-3 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block font-serif-body text-[13px] text-[#555] mb-1">Message</label>
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                className="w-full font-serif-body text-[15px] px-4 py-3 rounded-md border border-[#d7e6ea] focus:outline-none focus:ring-2 focus:ring-[#7ecbd6] focus:border-transparent resize-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="mt-5 flex items-center justify-center gap-2 bg-[#0f9fbc] hover:bg-[#008da9] disabled:opacity-70 text-white font-serif-body font-bold px-6 py-3 rounded-[4px] transition-colors"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>
    </PageLayout>
  );
}
